import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getNotifications,
  getUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '../api/client';
import type { InAppNotification } from '../api/client';
import { toast } from './use-toast';

const POLL_INTERVAL_MS = 30_000;

interface UseNotificationsReturn {
  notifications: InAppNotification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const useNotifications = (): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const previousUnreadCount = useRef(0);
  const knownIds = useRef(new Set<string>());

  const fetchNotifications = useCallback(async () => {
    try {
      const result = await getNotifications(50);
      setNotifications(result.notifications);
      setUnreadCount(result.unread_count);

      // Show toasts for new unread notifications
      if (previousUnreadCount.current > 0 || knownIds.current.size > 0) {
        for (const notif of result.notifications) {
          if (!notif.read && !knownIds.current.has(notif.id)) {
            toast({
              title: notif.title,
              description: notif.body,
            });
          }
        }
      }

      // Update known IDs
      knownIds.current = new Set(result.notifications.map((n) => n.id));
      previousUnreadCount.current = result.unread_count;
    } catch (err) {
      console.error('[useNotifications] Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const pollUnreadCount = useCallback(async () => {
    try {
      const result = await getUnreadCount();
      if (result.unread_count !== previousUnreadCount.current) {
        // Unread count changed — fetch full list to show toasts
        await fetchNotifications();
      }
    } catch (err) {
      console.error('[useNotifications] Poll error:', err);
    }
  }, [fetchNotifications]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Poll every 30s
  useEffect(() => {
    const interval = setInterval(pollUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [pollUnreadCount]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('[useNotifications] Mark as read error:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('[useNotifications] Mark all as read error:', err);
    }
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refresh: fetchNotifications,
  };
};
